/**
 * MCPServerConfig - Modal form component for creating/editing MCP server configurations
 *
 * Features:
 * - Name and description fields
 * - Transport type selection (stdio/sse/streamable-http)
 * - Stdio config fields: command, args, working directory
 * - SSE config fields: url, headers
 * - Streamable HTTP config fields: url, headers (AC-1801)
 * - Environment variables management (add/remove)
 * - Test Connection functionality
 * - Form validation
 * - Save/Cancel actions
 *
 * Technical Design: /workspace/docs/technical/TDD-MCP-Integration.md Section 3.2
 * Ticket: AC-778; AC-1801 (streamable-http edit support)
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2, Loader, CheckCircle, AlertCircle, Globe, FolderKanban } from 'lucide-react';
import apiService from '../../services/api';
import {
  MCPServer,
  MCPServerCreate,
  MCPServerUpdate,
  MCPTransportType,
  MCPServerStdioConfig,
  MCPServerSSEConfig,
  MCPServerStreamableHttpConfig,
  MCPServerTemplate,
} from '../../types';

interface MCPServerConfigProps {
  projectId?: number;
  server?: MCPServer;
  template?: MCPServerTemplate;
  scope?: 'user' | 'project';
  onClose: () => void;
  onSave: () => void;
}

interface EnvVar {
  name: string;
  value: string;
}

const MCPServerConfig: React.FC<MCPServerConfigProps> = ({
  projectId,
  server,
  template,
  scope = 'project',
  onClose,
  onSave,
}) => {
  // Determine scope from props or server context
  const effectiveScope = scope || (server?.scope as 'user' | 'project') || 'project';
  const isUserLevel = effectiveScope === 'user';
  const isEditMode = !!server;
  const isFromTemplate = !!template && !server;

  // Initialize form state - prefer server values, then template values, then empty
  const getInitialName = () => {
    if (server?.name) return server.name;
    if (template?.name) return template.name;
    return '';
  };

  const getInitialDescription = () => {
    if (server?.description) return server.description;
    if (template?.description) return template.description;
    return '';
  };

  const getInitialTransportType = (): MCPTransportType | '' => {
    if (server?.transport_type) return server.transport_type;
    if (template?.transport_type) return template.transport_type;
    return '';
  };

  const getInitialStdioCommand = () => {
    if (server?.stdio_config?.command) return server.stdio_config.command;
    if (template?.stdio_config?.command) return template.stdio_config.command;
    return '';
  };

  const getInitialStdioArgs = () => {
    if (server?.stdio_config?.args) return server.stdio_config.args.join(' ');
    if (template?.stdio_config?.args) return template.stdio_config.args.join(' ');
    return '';
  };

  // Form state
  const [name, setName] = useState(getInitialName());
  const [description, setDescription] = useState(getInitialDescription());
  const [transportType, setTransportType] = useState<MCPTransportType | ''>(
    getInitialTransportType()
  );

  // Stdio config state
  const [stdioCommand, setStdioCommand] = useState(getInitialStdioCommand());
  const [stdioArgs, setStdioArgs] = useState(getInitialStdioArgs());
  const [stdioCwd, setStdioCwd] = useState(server?.stdio_config?.cwd || '');

  // SSE config state
  const [sseUrl, setSseUrl] = useState(server?.sse_config?.url || '');
  const [sseHeaders, setSseHeaders] = useState<EnvVar[]>(
    server?.sse_config?.headers
      ? Object.entries(server.sse_config.headers).map(([name, value]) => ({ name, value }))
      : []
  );

  // Streamable HTTP config state (AC-1801). Loaded from an existing
  // streamable-http server so a provisioned Slack/remote server opens with its
  // URL pre-populated rather than a blank transport.
  const [httpUrl, setHttpUrl] = useState(server?.streamable_http_config?.url || '');
  const [httpHeaders, setHttpHeaders] = useState<EnvVar[]>(
    server?.streamable_http_config?.headers
      ? Object.entries(server.streamable_http_config.headers).map(([name, value]) => ({ name, value }))
      : []
  );

  // Environment variables state
  // In edit mode, we track existing env var names separately (values are not returned from API for security)
  // New env vars are added to envVars array
  // When using a template, pre-fill env var names from required_env_vars
  const getInitialEnvVars = (): EnvVar[] => {
    if (isFromTemplate && template?.required_env_vars) {
      return template.required_env_vars.map((envVar) => ({
        name: envVar.name,
        value: '',
      }));
    }
    return [];
  };

  const [envVars, setEnvVars] = useState<EnvVar[]>(getInitialEnvVars());

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Check if the form is valid for saving
   */
  const isFormValid = useMemo(() => {
    if (!name.trim()) return false;
    if (!transportType) return false;

    if (transportType === 'stdio') {
      if (!stdioCommand.trim()) return false;
    } else if (transportType === 'sse') {
      if (!sseUrl.trim()) return false;
    } else if (transportType === 'streamable-http') {
      if (!httpUrl.trim()) return false;
    }

    return true;
  }, [name, transportType, stdioCommand, sseUrl, httpUrl]);

  /**
   * Build the server create/update data object
   */
  const buildServerData = (): MCPServerCreate | MCPServerUpdate => {
    const data: MCPServerCreate = {
      name: name.trim(),
      description: description.trim() || undefined,
      transport_type: transportType as MCPTransportType,
    };

    if (transportType === 'stdio') {
      const stdioConfig: MCPServerStdioConfig = {
        command: stdioCommand.trim(),
        args: stdioArgs.trim() ? stdioArgs.trim().split(/\s+/) : [],
      };
      if (stdioCwd.trim()) {
        stdioConfig.cwd = stdioCwd.trim();
      }
      data.stdio_config = stdioConfig;
    } else if (transportType === 'sse') {
      const sseConfig: MCPServerSSEConfig = {
        url: sseUrl.trim(),
      };
      if (sseHeaders.length > 0) {
        const headers: Record<string, string> = {};
        sseHeaders.forEach((h) => {
          if (h.name.trim() && h.value.trim()) {
            headers[h.name.trim()] = h.value.trim();
          }
        });
        if (Object.keys(headers).length > 0) {
          sseConfig.headers = headers;
        }
      }
      data.sse_config = sseConfig;
    } else if (transportType === 'streamable-http') {
      // AC-1801: emit the {url, headers} shape the backend's
      // streamable_http_config expects, mirroring the SSE branch.
      const httpConfig: MCPServerStreamableHttpConfig = {
        url: httpUrl.trim(),
      };
      if (httpHeaders.length > 0) {
        const headers: Record<string, string> = {};
        httpHeaders.forEach((h) => {
          if (h.name.trim() && h.value.trim()) {
            headers[h.name.trim()] = h.value.trim();
          }
        });
        if (Object.keys(headers).length > 0) {
          httpConfig.headers = headers;
        }
      }
      data.streamable_http_config = httpConfig;
    }

    // Include env vars if any have values
    const validEnvVars = envVars.filter((ev) => ev.name.trim() && ev.value.trim());
    if (validEnvVars.length > 0) {
      data.env_vars = validEnvVars.map((ev) => ({
        name: ev.name.trim(),
        value: ev.value.trim(),
      }));
    }

    return data;
  };

  /**
   * Handle save button click
   */
  const handleSave = async () => {
    if (!isFormValid) return;

    try {
      setIsSaving(true);
      setError(null);

      const data = buildServerData();

      if (isUserLevel) {
        // User-level scope - use user-level API endpoints
        if (isEditMode && server) {
          await apiService.updateUserLevelMCPServer(server.mcp_server_id, data as MCPServerUpdate);
        } else {
          await apiService.createUserLevelMCPServer(data as MCPServerCreate);
        }
      } else {
        // Project-level scope - use project-level API endpoints
        if (!projectId) {
          throw new Error('Project ID is required for project-level MCP servers');
        }
        if (isEditMode && server) {
          await apiService.updateMCPServer(projectId, server.mcp_server_id, data as MCPServerUpdate);
        } else {
          await apiService.createMCPServer(projectId, data as MCPServerCreate);
        }
      }

      onSave();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save server configuration');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle test connection button click
   */
  const handleTestConnection = async () => {
    if (!isFormValid) return;

    try {
      setIsTesting(true);
      setTestResult(null);
      setError(null);

      const data = buildServerData() as MCPServerCreate;

      let result;
      if (isUserLevel) {
        // User-level scope - use user-level test endpoint
        result = await apiService.testUserLevelMCPConnection(data);
      } else {
        // Project-level scope - use project-level test endpoint
        if (!projectId) {
          throw new Error('Project ID is required for project-level MCP servers');
        }
        result = await apiService.testMCPConnection(projectId, data);
      }

      setTestResult({
        success: result.success,
        message: result.message,
      });
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.message || 'Connection test failed',
      });
    } finally {
      setIsTesting(false);
    }
  };

  /**
   * Handle adding an environment variable
   */
  const handleAddEnvVar = () => {
    setEnvVars([...envVars, { name: '', value: '' }]);
  };

  /**
   * Handle removing an environment variable
   */
  const handleRemoveEnvVar = (index: number) => {
    setEnvVars(envVars.filter((_, i) => i !== index));
  };

  /**
   * Handle updating an environment variable
   */
  const handleUpdateEnvVar = (index: number, field: 'name' | 'value', value: string) => {
    const updated = [...envVars];
    updated[index] = { ...updated[index], [field]: value };
    setEnvVars(updated);
  };

  /**
   * Handle adding an SSE header
   */
  const handleAddSseHeader = () => {
    setSseHeaders([...sseHeaders, { name: '', value: '' }]);
  };

  /**
   * Handle removing an SSE header
   */
  const handleRemoveSseHeader = (index: number) => {
    setSseHeaders(sseHeaders.filter((_, i) => i !== index));
  };

  /**
   * Handle updating an SSE header
   */
  const handleUpdateSseHeader = (index: number, field: 'name' | 'value', value: string) => {
    const updated = [...sseHeaders];
    updated[index] = { ...updated[index], [field]: value };
    setSseHeaders(updated);
  };

  /**
   * Handle adding a streamable-http header (AC-1801)
   */
  const handleAddHttpHeader = () => {
    setHttpHeaders([...httpHeaders, { name: '', value: '' }]);
  };

  /**
   * Handle removing a streamable-http header (AC-1801)
   */
  const handleRemoveHttpHeader = (index: number) => {
    setHttpHeaders(httpHeaders.filter((_, i) => i !== index));
  };

  /**
   * Handle updating a streamable-http header (AC-1801)
   */
  const handleUpdateHttpHeader = (index: number, field: 'name' | 'value', value: string) => {
    const updated = [...httpHeaders];
    updated[index] = { ...updated[index], [field]: value };
    setHttpHeaders(updated);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <h2 id="modal-title" className="text-lg font-semibold text-gray-900 dark:text-warm-white">
              {isEditMode
                ? 'Edit MCP Server'
                : isFromTemplate
                  ? `Add ${template.name}`
                  : 'Add MCP Server'}
            </h2>
            {/* Scope badge */}
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                isUserLevel
                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                  : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
              }`}
              data-testid="scope-badge"
            >
              {isUserLevel ? (
                <>
                  <Globe className="w-3 h-3" />
                  All Operations
                </>
              ) : (
                <>
                  <FolderKanban className="w-3 h-3" />
                  Project Only
                </>
              )}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded"
            aria-label="Close"
          >
            <AlertCircle data-testid="x-icon" className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <div className="px-6 py-4 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <AlertCircle data-testid="alert-circle-icon" className="w-5 h-5 text-red-600 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Test Result Message */}
          {testResult && (
            <div
              className={`p-3 border rounded-lg flex items-start gap-2 ${
                testResult.success
                  ? 'bg-green-50 border-green-200'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              }`}
            >
              {testResult.success ? (
                <CheckCircle data-testid="check-circle-icon" className="w-5 h-5 text-green-600 mt-0.5" />
              ) : (
                <AlertCircle data-testid="alert-circle-icon" className="w-5 h-5 text-red-600 mt-0.5" />
              )}
              <p className={`text-sm ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {testResult.message}
              </p>
            </div>
          )}

          {/* Server Name */}
          <div>
            <label htmlFor="server-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Server Name <span className="text-red-500">*</span>
            </label>
            <input
              id="server-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter server name"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              aria-label="Server Name"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="server-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              id="server-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description (optional)"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              aria-label="Description"
            />
          </div>

          {/* Transport Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" id="transport-type-label">
              Transport Type <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4" role="radiogroup" aria-labelledby="transport-type-label">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="transport-type"
                  value="stdio"
                  checked={transportType === 'stdio'}
                  onChange={() => setTransportType('stdio')}
                  className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 focus:ring-primary-500"
                  aria-label="stdio"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">stdio</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="transport-type"
                  value="sse"
                  checked={transportType === 'sse'}
                  onChange={() => setTransportType('sse')}
                  className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 focus:ring-primary-500"
                  aria-label="sse"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">sse</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="transport-type"
                  value="streamable-http"
                  checked={transportType === 'streamable-http'}
                  onChange={() => setTransportType('streamable-http')}
                  className="w-4 h-4 text-primary-600 border-gray-300 dark:border-gray-600 focus:ring-primary-500"
                  aria-label="streamable-http"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">streamable-http</span>
              </label>
            </div>
          </div>

          {/* Stdio Config Fields */}
          {transportType === 'stdio' && (
            <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Stdio Configuration</h3>

              <div>
                <label htmlFor="stdio-command" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Command <span className="text-red-500">*</span>
                </label>
                <input
                  id="stdio-command"
                  type="text"
                  value={stdioCommand}
                  onChange={(e) => setStdioCommand(e.target.value)}
                  placeholder="e.g., npx"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  aria-label="Command"
                />
              </div>

              <div>
                <label htmlFor="stdio-args" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Arguments
                </label>
                <input
                  id="stdio-args"
                  type="text"
                  value={stdioArgs}
                  onChange={(e) => setStdioArgs(e.target.value)}
                  placeholder="e.g., -y @anthropic/mcp-server-twilio"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  aria-label="Arguments"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Space-separated arguments</p>
              </div>

              <div>
                <label htmlFor="stdio-cwd" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Working Directory
                </label>
                <input
                  id="stdio-cwd"
                  type="text"
                  value={stdioCwd}
                  onChange={(e) => setStdioCwd(e.target.value)}
                  placeholder="Optional working directory path"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  aria-label="Working Directory"
                />
              </div>
            </div>
          )}

          {/* SSE Config Fields */}
          {transportType === 'sse' && (
            <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">SSE Configuration</h3>

              <div>
                <label htmlFor="sse-url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  URL <span className="text-red-500">*</span>
                </label>
                <input
                  id="sse-url"
                  type="text"
                  value={sseUrl}
                  onChange={(e) => setSseUrl(e.target.value)}
                  placeholder="e.g., https://api.example.com/mcp"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  aria-label="URL"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Headers</label>
                  <button
                    type="button"
                    onClick={handleAddSseHeader}
                    className="inline-flex items-center gap-1 px-2 py-1 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded"
                  >
                    <Plus className="w-4 h-4" />
                    Add Header
                  </button>
                </div>
                {sseHeaders.length > 0 && (
                  <div className="space-y-2">
                    {sseHeaders.map((header, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={header.name}
                          onChange={(e) => handleUpdateSseHeader(index, 'name', e.target.value)}
                          placeholder="Header name"
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                        />
                        <input
                          type="text"
                          value={header.value}
                          onChange={(e) => handleUpdateSseHeader(index, 'value', e.target.value)}
                          placeholder="Header value"
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveSseHeader(index)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          aria-label="Remove header"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Streamable HTTP Config Fields (AC-1801) */}
          {transportType === 'streamable-http' && (
            <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Streamable HTTP Configuration</h3>

              <div>
                <label htmlFor="http-url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  URL <span className="text-red-500">*</span>
                </label>
                <input
                  id="http-url"
                  type="text"
                  value={httpUrl}
                  onChange={(e) => setHttpUrl(e.target.value)}
                  placeholder="e.g., https://api.example.com/mcp"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  aria-label="URL"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Headers</label>
                  <button
                    type="button"
                    onClick={handleAddHttpHeader}
                    className="inline-flex items-center gap-1 px-2 py-1 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded"
                  >
                    <Plus className="w-4 h-4" />
                    Add Header
                  </button>
                </div>
                {httpHeaders.length > 0 && (
                  <div className="space-y-2">
                    {httpHeaders.map((header, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={header.name}
                          onChange={(e) => handleUpdateHttpHeader(index, 'name', e.target.value)}
                          placeholder="Header name"
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                        />
                        <input
                          type="text"
                          value={header.value}
                          onChange={(e) => handleUpdateHttpHeader(index, 'value', e.target.value)}
                          placeholder="Header value"
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveHttpHeader(index)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          aria-label="Remove header"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Environment Variables */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Environment Variables</label>
              <button
                type="button"
                onClick={handleAddEnvVar}
                className="inline-flex items-center gap-1 px-2 py-1 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded"
                aria-label="Add Variable"
              >
                <Plus data-testid="plus-icon" className="w-4 h-4" />
                Add Variable
              </button>
            </div>

            {/* Template helper text */}
            {isFromTemplate && template?.required_env_vars && template.required_env_vars.length > 0 && (
              <div className="mb-3 p-3 bg-primary-50 border border-primary-200 rounded-lg" data-testid="template-env-vars-help">
                <p className="text-sm text-blue-800 font-medium">
                  Required credentials for {template.name}:
                </p>
                <ul className="mt-2 space-y-1">
                  {template.required_env_vars.map((envVar) => (
                    <li key={envVar.name} className="text-sm text-blue-700">
                      <span className="font-mono">{envVar.name}</span>
                      {envVar.help_text && (
                        <span className="text-primary-600 ml-2">- {envVar.help_text}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {isEditMode && server?.env_var_names && server.env_var_names.length > 0 && (
              <div className="mb-3 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  Existing environment variables:{' '}
                  {server.env_var_names.map((name, i) => (
                    <React.Fragment key={name}>
                      <span className="font-mono">{name}</span>
                      {i < server.env_var_names.length - 1 ? ', ' : ''}
                    </React.Fragment>
                  ))}
                </p>
                <p className="text-xs text-primary-600 mt-1">
                  Add new variables below to update them (values are not displayed for security)
                </p>
              </div>
            )}

            {envVars.length > 0 && (
              <div className="space-y-2">
                {envVars.map((envVar, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={envVar.name}
                      onChange={(e) => handleUpdateEnvVar(index, 'name', e.target.value)}
                      placeholder="Variable name"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                      aria-placeholder="Variable name"
                    />
                    <input
                      type="password"
                      value={envVar.value}
                      onChange={(e) => handleUpdateEnvVar(index, 'value', e.target.value)}
                      placeholder="Variable value"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                      aria-placeholder="Variable value"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveEnvVar(index)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      aria-label="Remove variable"
                    >
                      <Trash2 data-testid="trash-icon" className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <button
            type="button"
            onClick={handleTestConnection}
            disabled={!isFormValid || isTesting || isSaving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-primary-600 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isTesting && <Loader data-testid="loader-icon" className="w-4 h-4 animate-spin" />}
            Test Connection
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!isFormValid || isSaving}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving && <Loader data-testid="loader-icon" className="w-4 h-4 animate-spin" />}
              <CheckCircle data-testid="save-icon" className="w-4 h-4" />
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MCPServerConfig;
